<template>
  <div class="suscripciones-container">
    <!-- Header -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">
          <i class="fas fa-credit-card me-2"></i>
          Gestión de Suscripciones
        </h1>
      </div>
      <button type="button" class="btn-primary-custom" @click="exportarReporte">
        <i class="fas fa-file-excel me-1"></i>
        Exportar
      </button>
    </div>

    <div class="content-wrapper">
      <!-- Métricas de suscripciones (solo activas, vencen, vencidas) -->
      <div class="metrics-grid">
        <div class="metric-card metric-active">
          <div class="metric-icon">
            <i class="fas fa-users"></i>
          </div>
          <div class="metric-content">
            <div class="metric-number">{{ metricas.total_activas }}</div>
            <div class="metric-label">Suscripciones Activas</div>
          </div>
        </div>
        <div class="metric-card metric-cancelled">
          <div class="metric-icon">
            <i class="fas fa-ban"></i>
          </div>
          <div class="metric-content">
            <div class="metric-number">{{ metricas.total_canceladas }}</div>
            <div class="metric-label">Suscripciones Canceladas</div>
          </div>
        </div>
        <div class="metric-card metric-warning">
          <div class="metric-icon">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <div class="metric-content">
            <div class="metric-number">{{ metricas.vencen_pronto }}</div>
            <div class="metric-label">Vencen en 7 días</div>
          </div>
        </div>
        <div class="metric-card metric-expired">
          <div class="metric-icon">
            <i class="fas fa-times-circle"></i>
          </div>
          <div class="metric-content">
            <div class="metric-number">{{ metricas.vencidas }}</div>
            <div class="metric-label">Vencidas</div>
          </div>
        </div>
      </div>

      <!-- Filtros -->
      <div class="filters-section">
        <div class="filters-compact">
          <div class="filters-fields">
            <div style="flex:1; min-width:120px;">
              <select class="filter-select" v-model="filtros.estado" style="width:100%">
                <option value="">Todos los estados</option>
                <option value="activa">Activa</option>
                <option value="cancelada">Cancelada</option>
                <option value="vencida">Vencida</option>
                <option value="suspendida">Suspendida</option>
              </select>
            </div>
            <div style="flex:1; min-width:120px;">
              <select class="filter-select" v-model="filtros.plan" style="width:100%">
                <option value="">Todos los planes</option>
                <option value="anual">Anual</option>
                <option value="mensual">Mensual</option>
              </select>
            </div>
            <div style="flex:2; min-width:180px;">
              <input type="text" class="filter-input" v-model="filtros.busqueda" placeholder="Buscar empresa o email..." style="width:100%">
            </div>
          </div>
          <div class="filters-actions">
            <button type="button" class="btn-icon" @click="aplicarFiltros" title="Filtrar">
              <i class="fas fa-filter"></i>
            </button>
            <button type="button" class="btn-icon btn-danger-outline" @click="limpiarFiltros" title="Limpiar filtros">
              <i class="fas fa-times"></i>
            </button>
            <button type="button" class="btn-icon btn-success-outline" @click="exportarReporte" title="Exportar a Excel">
              <i class="fas fa-file-excel"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Tabla de suscripciones -->
      <div class="table-section">
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Plan</th>
                <th>Estado</th>
                <th>Inicio</th>
                <th>Vencimiento</th>
                <th>Monto</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="suscripcion in suscripcionesFiltradas" :key="suscripcion.id">
                <td>
                  <div class="company-info">
                    <div class="company-name">{{ suscripcion.empresa_nombre }}</div>
                    <div class="company-ruc">{{ suscripcion.ruc }}</div>
                  </div>
                </td>
                <td>
                  <span class="badge" :class="getPlanBadgeClass(suscripcion.plan)">
                    {{ suscripcion.plan }}
                  </span>
                </td>
                <td>
                  <span class="badge" :class="getEstadoBadgeClass(suscripcion.estado)">
                    {{ suscripcion.estado }}
                  </span>
                </td>
                <td>{{ formatDate(suscripcion.inicio) }}</td>
                <td>
                  {{ formatDate(suscripcion.vencimiento) }}
                  <i v-if="proximoVencimiento(suscripcion.vencimiento)" 
                     class="bi bi-exclamation-triangle text-warning ms-1"
                     title="Vence pronto"></i>
                </td>
                <td>${{ suscripcion.monto }}</td>
                <td>
                  <div class="action-buttons">
                    <button 
                      v-if="!suscripcion.tiene_pago_periodo"
                      class="action-btn action-payment" 
                      @click="generarPago(suscripcion)" 
                      title="Generar Pago"
                    >
                      <i class="fas fa-credit-card"></i>
                    </button>
                    <button 
                      v-else
                      class="action-btn action-payment disabled" 
                      title="Ya existe pago para este período"
                      disabled
                    >
                      <i class="fas fa-credit-card"></i>
                    </button>
                    <button class="action-btn action-edit" @click="editarSuscripcion(suscripcion)" title="Cambiar plan">
                      <i class="fas fa-edit"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Detalle Suscripción -->
  <div class="modal-overlay" v-if="showDetalleModal" @click="showDetalleModal = false">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h3 class="modal-title">Detalle de Suscripción</h3>
        <button type="button" class="modal-close" @click="showDetalleModal = false">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="modal-body" v-if="suscripcionSeleccionada">
        <div class="modal-info-grid">
          <div class="info-section">
            <h4>Información de la Empresa</h4>
            <div class="info-item">
              <span class="info-label">Nombre:</span>
              <span class="info-value">{{ suscripcionSeleccionada.empresa_nombre }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Email:</span>
              <span class="info-value">{{ suscripcionSeleccionada.empresa_email }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Teléfono:</span>
              <span class="info-value">{{ suscripcionSeleccionada.empresa_telefono }}</span>
            </div>
          </div>
          <div class="info-section">
            <h4>Información de la Suscripción</h4>
            <div class="info-item">
              <span class="info-label">Plan:</span>
              <span class="info-value">{{ suscripcionSeleccionada.plan }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Estado:</span>
              <span class="info-value">{{ suscripcionSeleccionada.estado }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Monto:</span>
              <span class="info-value">${{ suscripcionSeleccionada.monto }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Ciclo:</span>
              <span class="info-value">{{ suscripcionSeleccionada.ciclo_facturacion }}</span>
            </div>
          </div>
        </div>
        
        <div class="modal-divider"></div>
        
        <div class="payments-section">
          <h4>Historial de Pagos</h4>
          <div class="payments-table">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Monto</th>
                  <th>Estado</th>
                  <th>Método</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="pago in suscripcionSeleccionada.pagos" :key="pago.id">
                  <td>{{ formatDate(pago.fecha) }}</td>
                  <td>${{ pago.monto }}</td>
                  <td>
                    <span class="status-badge" :class="pago.estado === 'completado' ? 'status-success' : 'status-danger'">
                      {{ pago.estado }}
                    </span>
                  </td>
                  <td>{{ pago.metodo_pago }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { notifySuccess, notifyError } from '@/services/notificationService'
import api from '@/services/api'

export default {
  name: 'SuscripcionesList',
  setup() {
    const suscripciones = ref([])
    const metricas = ref({
      total_activas: 0,
      ingresos_mes: '0.00',
      vencen_pronto: 0,
      nuevas_mes: 0
    })
    
    const filtros = ref({
      estado: '',
      plan: '',
      busqueda: ''
    })
    
    const showDetalleModal = ref(false)
    const suscripcionSeleccionada = ref(null)
    const isLoading = ref(false)

    const suscripcionesFiltradas = computed(() => {
      let filtered = suscripciones.value

      if (filtros.value.estado) {
        filtered = filtered.filter(s => s.estado === filtros.value.estado)
      }
      if (filtros.value.plan) {
        filtered = filtered.filter(s => s.plan === filtros.value.plan)
      }
      if (filtros.value.busqueda) {
        const busqueda = filtros.value.busqueda.toLowerCase()
        filtered = filtered.filter(s => 
          s.empresa_nombre.toLowerCase().includes(busqueda) ||
          s.empresa_email.toLowerCase().includes(busqueda)
        )
      }

      return filtered
    })

    const loadSuscripciones = async () => {
      try {
        isLoading.value = true
        const response = await api.get('/admin/suscripciones')
        
        if (response.data.success) {
          suscripciones.value = response.data.data || []
        } else {
          throw new Error(response.data.message || 'Error al cargar suscripciones')
        }
      } catch (error) {
        console.error('Error loading suscripciones:', error)
        notifyError('Error al cargar las suscripciones: ' + (error.response?.data?.message || error.message))
        suscripciones.value = []
      } finally {
        isLoading.value = false
      }
    }

    const loadMetricas = async () => {
      try {
        const response = await api.get('/admin/suscripciones/metricas')
        
        if (response.data.success) {
          metricas.value = response.data.data || metricas.value
        } else {
          throw new Error(response.data.message || 'Error al cargar métricas')
        }
      } catch (error) {
        console.error('Error loading métricas:', error)
        notifyError('Error al cargar las métricas: ' + (error.response?.data?.message || error.message))
      }
    }

    const verDetalle = async (suscripcion) => {
      try {
        const response = await api.get(`/admin/suscripciones/${suscripcion.id}/detalle`)
        
        if (response.data.success) {
          suscripcionSeleccionada.value = response.data.data
          showDetalleModal.value = true
        } else {
          throw new Error(response.data.message || 'Error al cargar detalle')
        }
      } catch (error) {
        console.error('Error loading detalle:', error)
        notifyError('Error al cargar el detalle: ' + (error.response?.data?.message || error.message))
      }
    }

    const editarSuscripcion = (suscripcion) => {
      // Implementar edición de suscripción
      console.log('Editar suscripción:', suscripcion)
    }

    const cancelarSuscripcion = async (id) => {
      const result = await window.Swal.fire({
        title: '¿Está seguro?',
        text: '¿Desea cancelar esta suscripción?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, cancelar',
        cancelButtonText: 'No, mantener'
      })

      if (!result.isConfirmed) {
        return
      }
      
      try {
        const response = await api.post(`/admin/suscripciones/${id}/cancelar`)
        
        if (response.data.success) {
          notifySuccess('Suscripción cancelada exitosamente')
          await loadSuscripciones()
          await loadMetricas()
        } else {
          throw new Error(response.data.message || 'Error al cancelar suscripción')
        }
      } catch (error) {
        console.error('Error cancelando suscripción:', error)
        notifyError('Error al cancelar la suscripción: ' + (error.response?.data?.message || error.message))
      }
    }

    const generarPago = async (suscripcion) => {
      const result = await window.Swal.fire({
        title: 'Generar Pago',
        text: `¿Generar pago para la suscripción de ${suscripcion.empresa_nombre} (${suscripcion.ruc})?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#28a745',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sí, generar pago',
        cancelButtonText: 'Cancelar'
      })

      if (!result.isConfirmed) {
        return
      }
      
      try {
        const response = await api.post(`/admin/suscripciones/${suscripcion.id}/generar-pago`)
        
        if (response.data.success) {
          notifySuccess('Pago generado exitosamente')
          await loadSuscripciones()
          await loadMetricas()
        } else {
          throw new Error(response.data.message || 'Error al generar pago')
        }
      } catch (error) {
        console.error('Error generando pago:', error)
        const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Error desconocido'
        notifyError('Error al generar el pago: ' + errorMessage)
      }
    }

    const aplicarFiltros = () => {
      // Los filtros se aplican automáticamente via computed
    }

    const limpiarFiltros = () => {
      filtros.value = {
        estado: '',
        plan: '',
        busqueda: ''
      }
    }

    const exportarReporte = async () => {
      try {
        const response = await api.get('/admin/suscripciones/exportar', {
          responseType: 'blob'
        })
        
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `suscripciones_${new Date().toISOString().split('T')[0]}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        notifySuccess('Reporte exportado exitosamente')
      } catch (error) {
        console.error('Error exportando reporte:', error)
        notifyError('Error al exportar el reporte: ' + (error.response?.data?.message || error.message))
      }
    }

    const getPlanBadgeClass = (plan) => {
      const classes = {
        basico: 'bg-secondary',
        profesional: 'bg-info',
        empresarial: 'bg-warning',
        premium: 'bg-success'
      }
      return classes[plan?.toLowerCase()] || 'bg-light'
    }

    const getEstadoBadgeClass = (estado) => {
      const classes = {
        activa: 'bg-success',
        vencida: 'bg-danger',
        cancelada: 'bg-secondary',
        suspendida: 'bg-warning'
      }
      return classes[estado] || 'bg-light'
    }

    const proximoVencimiento = (fecha) => {
      const vencimiento = new Date(fecha)
      const hoy = new Date()
      const diasRestantes = Math.ceil((vencimiento - hoy) / (1000 * 60 * 60 * 24))
      return diasRestantes <= 7 && diasRestantes > 0
    }

    const formatDate = (date) => {
      return date ? new Date(date).toLocaleDateString('es-ES') : '-'
    }

    onMounted(() => {
      loadSuscripciones()
      loadMetricas()
    })

    return {
      suscripciones,
      suscripcionesFiltradas,
      metricas,
      filtros,
      showDetalleModal,
      suscripcionSeleccionada,
      verDetalle,
      editarSuscripcion,
      cancelarSuscripcion,
      generarPago,
      aplicarFiltros,
      limpiarFiltros,
      exportarReporte,
      getPlanBadgeClass,
      getEstadoBadgeClass,
      proximoVencimiento,
      formatDate
    }
  }
}
</script>

<style>
/* Filtros compactos mejorados */
.filters-compact {
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: space-between;
}
.filters-fields {
  display: flex;
  gap: 0.5rem;
  flex: 1;
}
.filters-actions {
  display: flex;
  gap: 0.25rem;
  align-items: center;
}
.btn-icon {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background 0.2s;
  color: var(--text-primary);
}
.btn-icon:hover {
  background: var(--primary-color);
  color: #fff;
}
.btn-success-outline {
  border-color: var(--success-color);
  color: var(--success-color);
}
.btn-success-outline:hover {
  background: var(--success-color);
  color: #fff;
}
.btn-danger-outline {
  border-color: var(--danger-color);
  color: var(--danger-color);
}
.btn-danger-outline:hover {
  background: var(--danger-color);
  color: #fff;
}
/* Variables CSS para el tema */
:root {
  --primary-color: #3498db;
  --success-color: #27ae60;
  --warning-color: #f39c12;
  --danger-color: #e74c3c;
  --info-color: #17a2b8;
  --secondary-color: #6c757d;
}

/* Contenedor principal */
.suscripciones-container {
  background-color: var(--bg-main);
  color: var(--text-primary);
  min-height: 100vh;
  padding: 1.5rem;
}

/* Header de la página */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 2px 4px var(--shadow-color);
}

.header-content {
  flex: 1;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
}

.page-subtitle {
  font-size: 0.95rem;
  color: var(--text-secondary);
  margin: 0;
  display: flex;
  align-items: center;
}

/* Wrapper de contenido */
.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Grid de métricas */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.metric-card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px var(--shadow-color);
}

.metric-card:hover {
  box-shadow: 0 4px 12px var(--shadow-color);
  transform: translateY(-2px);
}

.metric-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.metric-icon i {
  font-size: 1.5rem;
  color: white;
}

.metric-active .metric-icon {
  background: linear-gradient(135deg, var(--success-color), #2ecc71);
}

.metric-income .metric-icon {
  background: linear-gradient(135deg, var(--primary-color), #2980b9);
}

.metric-warning .metric-icon {
  background: linear-gradient(135deg, var(--warning-color), #e67e22);
}

.metric-expired .metric-icon {
  background: linear-gradient(135deg, var(--danger-color), #c0392b);
}

.metric-cancelled .metric-icon {
  background: linear-gradient(135deg, var(--secondary-color), #6c757d);
}

.metric-content {
  flex: 1;
}

.metric-number {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
}

.metric-label {
  font-size: 0.9rem;
  color: var(--text-secondary);
  font-weight: 500;
}

/* Sección de filtros */
.filters-section {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1.5rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  box-shadow: 0 2px 4px var(--shadow-color);
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-actions {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 0.5rem;
}

.filter-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-primary);
}

.filter-select,
.filter-input {
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--input-bg);
  color: var(--text-primary);
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.filter-select:focus,
.filter-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
}

/* Botones personalizados */
.btn-primary-custom,
.btn-secondary-custom,
.btn-danger-outline {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;
}

.btn-primary-custom {
  background: linear-gradient(135deg, var(--primary-color), #2980b9);
  color: white;
}

.btn-primary-custom:hover {
  background: linear-gradient(135deg, #2980b9, var(--primary-color));
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
}

.btn-secondary-custom {
  background: var(--secondary-color);
  color: white;
}

.btn-secondary-custom:hover {
  background: #5a6268;
  transform: translateY(-1px);
}

.btn-danger-outline {
  background: transparent;
  color: var(--danger-color);
  border: 1px solid var(--danger-color);
}

.btn-danger-outline:hover {
  background: var(--danger-color);
  color: white;
}

/* Sección de tabla */
.table-section {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 2px 4px var(--shadow-color);
  overflow: hidden;
}

.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  background: var(--secondary-bg-main);
  color: var(--text-primary);
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.9rem;
  border-bottom: 2px solid var(--border-color);
  white-space: nowrap;
}

.data-table td {
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
  vertical-align: middle;
}

.data-table tbody tr {
  transition: background-color 0.2s ease;
}

.data-table tbody tr:hover {
  background: var(--hover-bg);
}

.company-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.company-name {
  font-weight: 600;
  color: var(--text-primary);
}

.company-email {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

/* Badges de estado */
.status-badge {
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-success {
  background: rgba(39, 174, 96, 0.1);
  color: var(--success-color);
  border: 1px solid rgba(39, 174, 96, 0.2);
}

.status-danger {
  background: rgba(231, 76, 60, 0.1);
  color: var(--danger-color);
  border: 1px solid rgba(231, 76, 60, 0.2);
}

.status-warning {
  background: rgba(243, 156, 18, 0.1);
  color: var(--warning-color);
  border: 1px solid rgba(243, 156, 18, 0.2);
}

/* Botones de acción */
.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  cursor: pointer;
}

.action-view {
  background: rgba(52, 152, 219, 0.1);
  color: var(--primary-color);
}

.action-view:hover {
  background: var(--primary-color);
  color: white;
  transform: scale(1.1);
}

.action-edit {
  background: rgba(243, 156, 18, 0.1);
  color: var(--warning-color);
}

.action-edit:hover {
  background: var(--warning-color);
  color: white;
  transform: scale(1.1);
}

.action-cancel {
  background: rgba(231, 76, 60, 0.1);
  color: var(--danger-color);
}

.action-cancel:hover {
  background: var(--danger-color);
  color: white;
  transform: scale(1.1);
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 2rem;
}

.modal-container {
  background: var(--card-bg);
  border-radius: 16px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid var(--border-color);
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: var(--danger-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.modal-close:hover {
  background: #c0392b;
  transform: scale(1.1);
}

.modal-body {
  padding: 2rem;
}

.modal-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

.info-section h4 {
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--primary-color);
}

.info-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border-color);
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: 600;
  color: var(--text-secondary);
  flex-shrink: 0;
  min-width: 100px;
}

.info-value {
  color: var(--text-primary);
  text-align: right;
}

.modal-divider {
  height: 1px;
  background: var(--border-color);
  margin: 2rem 0;
}

.payments-section h4 {
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.payments-table {
  overflow-x: auto;
}

/* Responsive */
@media (max-width: 768px) {
  .suscripciones-container {
    padding: 1rem;
  }
  
  .page-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .metrics-grid {
    grid-template-columns: 1fr;
  }
  
  .filters-section {
    grid-template-columns: 1fr;
  }
  
  .filter-actions {
    flex-direction: column;
  }
  
  .modal-overlay {
    padding: 1rem;
  }
  
  .modal-info-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .info-item {
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .info-value {
    text-align: left;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 1.5rem;
  }
  
  .metric-number {
    font-size: 1.5rem;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 0.25rem;
  }
}
</style>
